import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import type { UnifiedMessage } from '@/types/unified-messaging';
import { formatDistanceToNow } from 'date-fns';
import { fr, nl } from 'date-fns/locale';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { ReactionPicker } from './ReactionPicker';
import { MessageContextMenu } from './MessageContextMenu';
import { PlayCircle, Dumbbell, ExternalLink, Video, X, Clock, Target, ChevronRight, MessageSquare, Info, Play, Pause } from 'lucide-react';
import { getExerciseCategoryImage } from '@/components/coaching/constants';
import { ExerciseDetailModal } from '@/components/coaching/player/ExerciseDetailModal';
import type { Exercise, ExerciseCategory, ExerciseDifficulty } from '@/types/coaching.types';

// Category styles matching StudentDetail.tsx
const EXERCISE_CATEGORY_STYLES: Record<string, { label: string; gradient: string; badge: string }> = {
  driving: { label: 'Driving', gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 50%, #B91C1C 100%)', badge: 'bg-red-100 text-red-700' },
  iron_play: { label: 'Iron Play', gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%)', badge: 'bg-blue-100 text-blue-700' },
  short_game: { label: 'Short Game', gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 50%, #15803D 100%)', badge: 'bg-green-100 text-green-700' },
  putting: { label: 'Putting', gradient: 'linear-gradient(135deg, #A855F7 0%, #9333EA 50%, #7E22CE 100%)', badge: 'bg-purple-100 text-purple-700' },
  bunker: { label: 'Bunker', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #B45309 100%)', badge: 'bg-amber-100 text-amber-700' },
  mental: { label: 'Mental', gradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 50%, #4338CA 100%)', badge: 'bg-indigo-100 text-indigo-700' },
  fitness: { label: 'Fitness', gradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 50%, #C2410C 100%)', badge: 'bg-orange-100 text-orange-700' },
  warm_up: { label: 'Warm-up', gradient: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 50%, #0F766E 100%)', badge: 'bg-teal-100 text-teal-700' },
};
const DEFAULT_CATEGORY_STYLE = { label: 'Exercise', gradient: 'linear-gradient(135deg, #6B7280 0%, #4B5563 50%, #374151 100%)', badge: 'bg-gray-100 text-gray-700' };

const getCategoryLetter = (category?: string): string => {
  if (!category) return 'E';
  return category.charAt(0).toUpperCase();
};

// ---------------------------------------------------------------------------
// Custom Voice Message Player
// ---------------------------------------------------------------------------
// Pseudo-random but stable bar heights from the message src string
function waveformHeights(seed: string, count: number): number[] {
  const heights: number[] = [];
  for (let i = 0; i < count; i++) {
    const code = seed.charCodeAt(i % seed.length) + i * 7;
    heights.push(30 + (code % 70)); // 30–100% range
  }
  return heights;
}

const BAR_COUNT = 28;

function VoiceMessagePlayer({ src, duration, isOwn }: { src: string; duration?: number; isOwn: boolean }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [total, setTotal] = useState(duration ?? 0);
  const rafRef = useRef<number>(0);
  const bars = waveformHeights(src, BAR_COUNT);

  const tick = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setElapsed(audio.currentTime);
    if (!audio.paused) rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onLoaded = () => { if (!duration) setTotal(isFinite(audio.duration) ? audio.duration : 0); };
    const onEnded = () => { setIsPlaying(false); setIsLoading(false); setElapsed(0); };
    const onPlaying = () => { setIsLoading(false); setIsPlaying(true); setHasError(false); rafRef.current = requestAnimationFrame(tick); };
    const onWaiting = () => { setIsLoading(true); };
    const onCanPlay = () => { setIsLoading(false); };
    const onError = () => { setIsLoading(false); setIsPlaying(false); setHasError(true); };
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onError);
      cancelAnimationFrame(rafRef.current);
    };
  }, [duration, tick]);

  const toggle = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      // Reload the source so the browser retries the network request on error
      if (hasError) {
        audio.load();
      }
      setHasError(false);
      setIsLoading(true);
      audio.play().catch(() => { setIsLoading(false); setHasError(true); });
    } else {
      audio.pause();
      setIsPlaying(false);
      setIsLoading(false);
      cancelAnimationFrame(rafRef.current);
    }
  };

  const progress = total > 0 ? elapsed / total : 0;
  const filledBars = Math.round(progress * BAR_COUNT);
  const fmt = (s: number) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;

  const activeColor = isOwn ? 'rgba(255,255,255,0.95)' : '#2A4494';
  const inactiveColor = isOwn ? 'rgba(255,255,255,0.30)' : 'rgba(42,68,148,0.25)';
  const btnColor = isOwn ? 'rgba(255,255,255,0.15)' : 'rgba(42,68,148,0.12)';
  const iconColor = isOwn ? '#ffffff' : '#2A4494';
  const timeColor = isOwn ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.35)';
  const errorColor = isOwn ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.35)';

  // Single audio element always in the DOM — never conditionally swapped out,
  // so audioRef stays valid across the error→retry re-render cycle.
  // No crossOrigin attr: CORS headers on R2 may not be set, and audio playback
  // doesn't need them — adding it causes browser to block the request silently.
  const audioEl = <audio ref={audioRef} src={src} preload="none" />;

  if (hasError) {
    return (
      <div className="flex items-center gap-2">
        {audioEl}
        <button
          data-interactive
          onClick={toggle}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); toggle(e); }}
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform duration-150"
          style={{ background: btnColor }}
          title="Retry"
        >
          <Play className="w-3.5 h-3.5 ml-0.5" style={{ color: iconColor }} />
        </button>
        <span className="text-[11px]" style={{ color: errorColor }}>Tap to retry</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2.5"
      onClick={(e) => e.stopPropagation()}
    >
      {audioEl}

      {/* Play / pause / loading */}
      <button
        data-interactive
        onClick={toggle}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); toggle(e); }}
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform duration-150 relative"
        style={{ background: btnColor }}
      >
        {isLoading ? (
          /* Spinner ring */
          <svg
            className="w-4 h-4 animate-spin"
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle
              cx="8" cy="8" r="6"
              stroke={iconColor}
              strokeOpacity="0.25"
              strokeWidth="2"
            />
            <path
              d="M8 2a6 6 0 0 1 6 6"
              stroke={iconColor}
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : isPlaying ? (
          <Pause className="w-3.5 h-3.5" style={{ color: iconColor }} />
        ) : (
          <Play className="w-3.5 h-3.5 ml-0.5" style={{ color: iconColor }} />
        )}
      </button>

      {/* Waveform bars — animate when loading */}
      <div className="flex items-center gap-px flex-1" style={{ height: 28 }}>
        {bars.map((h, i) => (
          <div
            key={i}
            className={`flex-1 rounded-full transition-colors duration-75 ${isLoading ? 'animate-pulse' : ''}`}
            style={{
              height: `${h}%`,
              background: i < filledBars ? activeColor : inactiveColor,
            }}
          />
        ))}
      </div>

      {/* Duration */}
      <span className="flex-shrink-0 text-[11px] tabular-nums" style={{ color: timeColor }}>
        {isPlaying ? fmt(elapsed) : fmt(total)}
      </span>
    </div>
  );
}

interface UnifiedMessageBubbleProps {
  message: UnifiedMessage;
  isOwn: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  isMiddleInGroup: boolean;
  isEditing: boolean;
  editContent: string;
  onEditChange: (content: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  showReactionPicker: boolean;
  longPressedMessage: string | null;
  reactingMessages: Set<string>;
  onReaction: (messageId: string, emoji: string) => void;
  onEdit: (messageId: string) => void;
  onDelete: (messageId: string) => void;
  onReply: (messageId: string) => void;
  onConvertToTask?: (message: { id: string; content: string; sender_name?: string }) => void;
  onLongPressStart: (messageId: string, e: React.TouchEvent | React.MouseEvent) => void;
  onLongPressEnd: () => void;
  onMessageTap: (messageId: string) => void;
  onDismissContextMenu?: () => void;
  showTimestamp: boolean;
  allMessages: UnifiedMessage[];
  language: string;
  bubbleRect?: { x: number; y: number; width: number; height: number };
}

export function UnifiedMessageBubble({
  message,
  isOwn,
  isFirstInGroup,
  isLastInGroup,
  isMiddleInGroup,
  isEditing,
  editContent,
  onEditChange,
  onSaveEdit,
  onCancelEdit,
  showReactionPicker,
  longPressedMessage,
  reactingMessages,
  onReaction,
  onEdit,
  onDelete,
  onReply,
  onConvertToTask,
  onLongPressStart,
  onLongPressEnd,
  onMessageTap,
  onDismissContextMenu,
  showTimestamp,
  allMessages,
  language,
  bubbleRect,
}: UnifiedMessageBubbleProps) {
  const { t } = useAppTranslations();

  // Helper function to format dates with proper locale
  const formatDateWithLocale = (date: Date) => {
    const localeMap = { fr, nl, en: undefined };
    return formatDistanceToNow(date, { addSuffix: true, locale: localeMap[language as keyof typeof localeMap] });
  };

  const findOriginalMessage = (replyToId: string) => {
    return allMessages.find(m => m.id === replyToId);
  };

  const scrollToMessage = (messageId: string) => {
    const element = document.querySelector(`[data-message-id="${messageId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.style.backgroundColor = '#fef3c7';
      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 1500);
    }
  };

  const renderReplyPreview = (originalMessage: UnifiedMessage) => {
    const origData = originalMessage.message_data as Record<string, any> | undefined;
    const replyThumb =
      originalMessage.message_type === 'photo'
        ? (origData?.image_urls?.[0] as string | undefined)
        : originalMessage.message_type === 'video'
        ? (origData?.video_thumbnail_url as string | undefined)
        : undefined;

    const isVoiceReply = originalMessage.message_type === 'voice';
    const voiceDuration = origData?.voice_duration as number | undefined;
    const voiceSrc = (origData?.voice_url as string | undefined) ?? '';
    const voiceBars = isVoiceReply ? waveformHeights(voiceSrc, 20) : [];
    const fmtDuration = (s: number) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;

    const isExerciseReply = originalMessage.message_type === 'exercise_share';
    const replyExerciseName = isExerciseReply ? (origData?.exercise_name as string | undefined) : undefined;

    const replyLabel =
      originalMessage.message_type === 'photo' ? 'Photo'
      : originalMessage.message_type === 'video' ? 'Video'
      : isExerciseReply ? (replyExerciseName || 'Exercise')
      : originalMessage.content?.substring(0, 80) || '…';

    const showEllipsis =
      !replyThumb &&
      !isVoiceReply &&
      !isExerciseReply &&
      originalMessage.message_type === 'text' &&
      (originalMessage.content?.length ?? 0) > 80;

    const accentColor = isOwn ? 'rgba(255,255,255,0.85)' : '#52B788';
    const barActive = isOwn ? 'rgba(255,255,255,0.85)' : '#2A4494';
    const barInactive = isOwn ? 'rgba(255,255,255,0.30)' : 'rgba(42,68,148,0.25)';
    const iconColor = isOwn ? '#ffffff' : '#2A4494';

    return (
      <div
        className="flex gap-0 rounded-xl overflow-hidden cursor-pointer active:opacity-70 transition-opacity"
        style={{ background: isOwn ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.07)' }}
        onClick={() => scrollToMessage(originalMessage.id)}
      >
        {/* thick left accent bar */}
        <div
          className="w-1 flex-shrink-0 rounded-l-xl"
          style={{ background: accentColor }}
        />
        <div className="px-2.5 py-2 min-w-0 flex-1">
          <p className={`text-xs font-bold leading-tight mb-0.5 ${isOwn ? 'text-white' : 'text-emerald-700'}`}>
            {originalMessage.sender?.display_name || 'Unknown'}
          </p>
          {isVoiceReply ? (
            <div className="flex items-center gap-1.5 mt-0.5">
              {/* Static play icon */}
              <div
                className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: isOwn ? 'rgba(255,255,255,0.15)' : 'rgba(42,68,148,0.12)' }}
              >
                <Play className="w-2.5 h-2.5 ml-px" style={{ color: iconColor }} />
              </div>
              {/* Mini waveform */}
              <div className="flex items-center gap-px flex-1" style={{ height: 16 }}>
                {voiceBars.map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-full"
                    style={{ height: `${h}%`, background: barInactive }}
                  />
                ))}
              </div>
              {/* Duration */}
              {voiceDuration != null && (
                <span className="flex-shrink-0 text-[10px] tabular-nums" style={{ color: isOwn ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.35)' }}>
                  {fmtDuration(voiceDuration)}
                </span>
              )}
            </div>
          ) : isExerciseReply ? (
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className="flex-shrink-0 w-5 h-5 rounded-lg flex items-center justify-center"
                style={{ background: isOwn ? 'rgba(255,255,255,0.15)' : 'rgba(42,68,148,0.12)' }}
              >
                <Dumbbell className="w-3 h-3" style={{ color: iconColor }} />
              </div>
              <p className={`text-xs leading-snug truncate ${isOwn ? 'text-white/75' : 'text-slate-500'}`}>
                {replyLabel}
              </p>
            </div>
          ) : (
            <p className={`text-xs leading-snug truncate ${isOwn ? 'text-white/75' : 'text-slate-500'}`}>
              {replyLabel}{showEllipsis ? '…' : ''}
            </p>
          )}
        </div>
        {replyThumb && (
          <div className="flex-shrink-0 w-12 h-12 relative">
            <img
              src={replyThumb}
              alt=""
              className="w-full h-full object-cover"
            />
            {originalMessage.message_type === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <PlayCircle className="w-5 h-5 text-white drop-shadow" />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const hasReactions = !!(message.reaction_counts && Object.keys(message.reaction_counts).some(k => Number((message.reaction_counts as Record<string,number>)[k]) > 0));

  // Compute the lifted bubble content for the long-press context menu
  // Mirrors the real bubble exactly so the lifted clone looks identical.
  const liftedBubbleNode: React.ReactNode = (() => {
    const bubbleData = message.message_data as Record<string, any> | undefined;
    const ownBg = { background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)', boxShadow: '0 4px 12px rgba(42,68,148,0.15)' };
    const otherBg = { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' };
    const bubbleStyle = isOwn ? ownBg : otherBg;
    const roundingCls = isOwn
      ? `text-white ${isFirstInGroup ? 'rounded-2xl rounded-br-md' : isLastInGroup ? 'rounded-2xl rounded-bl-md' : 'rounded-2xl'}`
      : `bg-white text-gray-900 border border-gray-100 ${isFirstInGroup ? 'rounded-2xl rounded-bl-md' : isLastInGroup ? 'rounded-2xl rounded-br-md' : 'rounded-2xl'}`;

    const originalMsg = message.reply_to_id ? findOriginalMessage(message.reply_to_id) : null;

    if (message.message_type === 'photo') {
      return (
        <div className={`overflow-hidden ${roundingCls}`} style={bubbleStyle}>
          {originalMsg && <div className="px-3 pt-2">{renderReplyPreview(originalMsg)}</div>}
          {(message.message_data as any)?.image_urls?.map((url: string, index: number) => (
            <img key={index} src={url} alt="photo" className="w-full h-auto block" style={{ maxHeight: '300px', objectFit: 'cover' }} />
          ))}
          {message.content && (
            <div className={`px-3 py-2 text-sm leading-relaxed ${isOwn ? 'text-white' : 'text-gray-900'}`}>{message.content}</div>
          )}
        </div>
      );
    }
    if (message.message_type === 'voice') {
      return (
        <div className={`w-52 px-4 py-3 ${roundingCls}`} style={bubbleStyle}>
          {originalMsg && <div className="mb-2">{renderReplyPreview(originalMsg)}</div>}
          <VoiceMessagePlayer
            src={(message.message_data as any)?.voice_url}
            duration={(message.message_data as any)?.voice_duration}
            isOwn={isOwn}
          />
        </div>
      );
    }
    if (message.message_type === 'video') {
      const thumb = typeof bubbleData?.video_thumbnail_url === 'string' ? bubbleData.video_thumbnail_url : undefined;
      return (
        <div className={`overflow-hidden ${roundingCls}`} style={bubbleStyle}>
          {originalMsg && <div className="px-3 pt-2">{renderReplyPreview(originalMsg)}</div>}
          <div className="relative w-full h-[120px] bg-black/30 flex items-center justify-center">
            {thumb && <img src={thumb} alt="video" className="absolute inset-0 w-full h-full object-cover opacity-70" />}
            <PlayCircle className="w-10 h-10 text-white drop-shadow-md relative z-10" />
          </div>
        </div>
      );
    }
    if (message.message_type === 'exercise_share') {
      const exName = typeof bubbleData?.exercise_name === 'string' ? bubbleData.exercise_name : 'Exercise';
      const exCat = typeof bubbleData?.exercise_category === 'string' ? bubbleData.exercise_category : undefined;
      const exDesc = typeof bubbleData?.exercise_description === 'string' ? bubbleData.exercise_description : undefined;
      const assignmentStatus = typeof bubbleData?.assignment_status === 'string' ? bubbleData.assignment_status : undefined;
      const statusLabel = (s?: string) => {
        switch (s) {
          case 'completed': return 'Completed';
          case 'in_progress': return 'In Progress';
          case 'assigned': return 'Assigned';
          default: return s || '';
        }
      };
      return (
        <div className={`px-4 py-1 ${roundingCls}`} style={bubbleStyle}>
          {originalMsg && <div className="mb-2">{renderReplyPreview(originalMsg)}</div>}
          <div className="space-y-2">
            <div className={`rounded-xl overflow-hidden border ${isOwn ? 'border-white/20 bg-white/10' : 'border-gray-200 bg-gray-50'}`}>
              <div className="p-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isOwn ? 'bg-white/20' : 'bg-indigo-100'}`}>
                    <Dumbbell className={`w-4 h-4 ${isOwn ? 'text-white' : 'text-indigo-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${isOwn ? 'text-white' : 'text-gray-900'}`}>{exName}</div>
                    <div className="flex items-center gap-2">
                      {exCat && (
                        <span className={`text-[10px] capitalize ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                          {exCat.replace('_', ' ')}
                        </span>
                      )}
                      {assignmentStatus && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isOwn ? 'text-white/80 bg-white/15' : 'text-blue-600 bg-blue-50'}`}>
                          {statusLabel(assignmentStatus)}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isOwn ? 'text-white/50' : 'text-gray-400'}`} />
                </div>
                {exDesc && (
                  <div className={`text-xs leading-relaxed line-clamp-2 ${isOwn ? 'text-white/80' : 'text-gray-600'}`}>
                    {exDesc}
                  </div>
                )}
              </div>
            </div>
          </div>
          {message.content && (
            <div className={`text-sm leading-relaxed ${isOwn ? 'text-white' : 'text-gray-900'} px-1`}>
              {message.content}
            </div>
          )}
        </div>
      );
    }
    // Default: text
    return (
      <div className={`px-4 py-1 ${roundingCls}`} style={bubbleStyle}>
        <div className={`text-sm leading-relaxed ${isOwn ? 'text-white' : 'text-gray-900'}`}>
          {originalMsg && <div className="-mx-3 mb-1 rounded-t-xl overflow-hidden">{renderReplyPreview(originalMsg)}</div>}
          {message.content}
        </div>
      </div>
    );
  })();

  return (
    <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${hasReactions ? 'mb-4' : 'mb-1'}`}>
      {/* Avatar - only show for first message in group from others */}
      {!isOwn && isFirstInGroup ? (
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
          <Avatar className="w-8 h-8">
            <AvatarImage src={message.sender?.avatar_url || undefined} />
            <AvatarFallback className="bg-gray-300 text-gray-600 text-xs font-medium shadow-sm">
              {message.sender?.display_name?.[0] || 'U'}
            </AvatarFallback>
          </Avatar>
        </div>
      ) : !isOwn ? (
        // Spacer for subsequent messages to align with avatar position (w-8 + mr-3 = 44px = ml-11)
        <div className="w-11 flex-shrink-0"></div>
      ) : null}

      {/* Message bubble and interactions container */}
      <div className={`max-w-xs lg:max-w-md ${isOwn ? 'ml-auto' : 'mr-auto'}`}>
        {/* Message content or edit input */}
        {isEditing ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-3 shadow-sm">
            <input
              type="text"
              value={editContent}
              onChange={(e) => onEditChange(e.target.value)}
              className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1B4332] focus:border-transparent"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSaveEdit();
                if (e.key === 'Escape') onCancelEdit();
              }}
            />
            <div className="flex space-x-2 mt-2">
              <button
                onClick={onSaveEdit}
                className="px-3 py-1 tap-target inline-flex items-center justify-center bg-gradient-to-r from-[#2A4494] to-[#1E3270] text-white text-xs rounded-full hover:from-[#1E3270] hover:to-[#2A4494] transition-colors shrink-0"
              >
                Save
              </button>
              <button
                onClick={onCancelEdit}
                className="px-3 py-1 tap-target inline-flex items-center justify-center bg-gray-200 text-gray-700 text-xs rounded-full hover:bg-gray-300 transition-colors shrink-0"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className="relative"
            data-message-id={message.id}
            onTouchStart={(e) => {
              const target = e.target as HTMLElement;
              const isInteractive = target.closest('button, video, iframe, [data-interactive]');
              if (isInteractive) return;
              onLongPressStart(message.id, e);
            }}
            onTouchEnd={(e) => {
              const target = e.target as HTMLElement;
              const isInteractive = target.closest('button, video, iframe, [data-interactive]');
              if (isInteractive) return;
              onLongPressEnd();
            }}
            onMouseDown={(e) => {
              // Only prevent default if it's a right-click or we want to prevent selection
              if (e.button === 0) { // Left mouse button
                const target = e.target as HTMLElement;
                const isInteractive = target.closest('button, video, iframe, [data-interactive]');
                if (isInteractive) return;
                onLongPressStart(message.id, e);
              }
            }}
            onMouseUp={(e) => {
              if (e.button === 0) {
                const target = e.target as HTMLElement;
                const isInteractive = target.closest('button, video, iframe, [data-interactive]');
                if (isInteractive) return;
                onLongPressEnd();
              }
            }}
            onPointerLeave={(e) => { if (e.pointerType !== 'touch') onLongPressEnd(); }}
            onTouchCancel={onLongPressEnd}
            onClick={(e) => {
              // Only prevent if we're not in a long-press state
              if (!showReactionPicker) {
                onMessageTap(message.id);
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault(); // Prevent browser context menu
            }}
            style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
          >
            {/* Reaction picker - ABOVE the message */}
            {showReactionPicker && (
              <ReactionPicker
                messageId={message.id}
                onReaction={onReaction}
              />
            )}

            <div
              className={`relative group cursor-pointer message-bubble ${
                isOwn
                  ? `text-white shadow-lg hover:shadow-xl ${
                      isFirstInGroup ? 'rounded-2xl rounded-br-md' : isLastInGroup ? 'rounded-2xl rounded-bl-md' : 'rounded-2xl'
                    }`
                  : `bg-white text-gray-900 shadow-md hover:shadow-lg ${
                      isFirstInGroup ? 'rounded-2xl rounded-bl-md' : isLastInGroup ? 'rounded-2xl rounded-br-md' : 'rounded-2xl'
                    }`
              } ${message.message_type === 'photo' ? 'overflow-hidden' : message.message_type === 'voice' ? 'w-52' : 'px-4 py-1'} transition-all duration-200 ease-out`}
              style={{
                ...(isOwn
                  ? {
                      background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)',
                      boxShadow: '0 4px 12px rgba(42, 68, 148, 0.15)'
                    }
                  : {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                    }),
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                // When lifted, hide the original in-flow bubble; the portal renders a clone above the scrim
                ...(longPressedMessage === message.id ? { opacity: 0 } : {})
              }}
            >
              {/* Photo message — image bleeds to bubble edges */}
              {message.message_type === 'photo' ? (
                <div>
                  {/* Reply preview sits inside with padding */}
                  {message.reply_to_id && (() => {
                    const originalMessage = findOriginalMessage(message.reply_to_id);
                    return originalMessage ? <div className="px-3 pt-2">{renderReplyPreview(originalMessage)}</div> : null;
                  })()}

                  {(message.message_data as any)?.image_urls?.map((url: string, index: number) => (
                    <div key={index} className="relative">
                      <img
                        src={url}
                        alt={t.messaging.sharedPhoto}
                        className="w-full h-auto block"
                        loading="lazy"
                        style={{ maxHeight: '300px', objectFit: 'cover' }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
                    </div>
                  ))}

                  {message.content && (
                    <div className={`px-3 py-2 text-sm leading-relaxed ${isOwn ? 'text-white' : 'text-gray-900'}`}>
                      {message.content}
                    </div>
                  )}
                </div>
              ) : message.message_type === 'voice' ? (
                /* Voice message — content lives directly inside the bubble (no extra wrapper) */
                <div className="px-4 py-3">
                  {message.reply_to_id && (() => {
                    const originalMessage = findOriginalMessage(message.reply_to_id);
                    return originalMessage ? <div className="mb-2">{renderReplyPreview(originalMessage)}</div> : null;
                  })()}
                  <VoiceMessagePlayer
                    src={(message.message_data as any)?.voice_url}
                    duration={(message.message_data as any)?.voice_duration}
                    isOwn={isOwn}
                  />
                </div>
              ) : message.message_type === 'video' ? (
                /* Video message */
                <VideoMessageContent message={message} isOwn={isOwn} findOriginalMessage={findOriginalMessage} renderReplyPreview={renderReplyPreview} />
              ) : message.message_type === 'exercise_share' ? (
                /* Exercise share card */
                <ExerciseShareContent message={message} isOwn={isOwn} findOriginalMessage={findOriginalMessage} renderReplyPreview={renderReplyPreview} />
              ) : (
                /* Regular text message */
                <div className={`text-sm leading-relaxed ${isOwn ? 'text-white' : 'text-gray-900'}`}>
                  {/* Reply Preview in Message */}
                  {message.reply_to_id && (() => {
                    const originalMessage = findOriginalMessage(message.reply_to_id);
                    return originalMessage ? <div className="-mx-3 mb-1 rounded-t-xl overflow-hidden">{renderReplyPreview(originalMessage)}</div> : null;
                  })()}

                  {message.content}
                  {message.edited_at && (
                    <span className={`text-xs ml-2 opacity-75 ${isOwn ? 'text-blue-100' : 'text-gray-500'}`}>
                      (edited)
                    </span>
                  )}
                </div>
              )}

              {/* Subtle indicator for long press */}
              <div
                className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none ${
                  isOwn ? 'bg-white/10' : 'bg-gray-50/50'
                }`}
              />
            </div>

            {/* Reactions — outside overflow-hidden bubble so pill is never clipped */}
            {hasReactions && (
              <div className={`absolute -bottom-3 flex items-center gap-0.5 z-10 ${isOwn ? 'right-2' : 'left-2'}`}>
                <div
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                  style={{
                    background: 'rgba(255,255,255,0.97)',
                    border: '1.5px solid rgba(15,23,42,0.09)',
                    boxShadow: '0 2px 8px rgba(15,23,42,0.13)',
                  }}
                >
                  {Object.entries(message.reaction_counts as Record<string, number>)
                    .filter(([, count]) => Number(count) > 0)
                    .map(([emoji, count]) => (
                      <button
                        key={emoji}
                        onClick={(e) => { e.stopPropagation(); onReaction(message.id, emoji); }}
                        disabled={reactingMessages.has(message.id)}
                        className={`flex items-center gap-0.5 transition-all duration-150 hover:scale-110 active:scale-95 disabled:opacity-50 ${message.user_has_reacted?.[emoji] ? 'opacity-100' : 'opacity-80'}`}
                      >
                        <span className="text-sm leading-none">{emoji}</span>
                        {Number(count) > 1 && (
                          <span className="text-[11px] font-semibold text-slate-600 leading-none">{count}</span>
                        )}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Context Menu - only show for this specific message */}
            {longPressedMessage === message.id && (
              <MessageContextMenu
                messageId={message.id}
                isOwnMessage={isOwn}
                messageContent={message.content || ''}
                onReply={() => onReply(message.id)}
                onEdit={() => onEdit(message.id)}
                onDelete={() => onDelete(message.id)}
                onCopy={() => navigator.clipboard.writeText(message.content || '')}
                onReaction={(emoji) => onReaction(message.id, emoji)}
                onConvertToTask={onConvertToTask ? () => onConvertToTask({ id: message.id, content: message.content || '', sender_name: message.sender?.display_name }) : undefined}
                onDismiss={onDismissContextMenu}
                bubbleRect={bubbleRect}
                liftedBubble={liftedBubbleNode}
              />
            )}

            {/* Removed flat reaction pills — now rendered inside bubble as WhatsApp-style overlay */}
            {false && message.reaction_counts && Object.keys(message.reaction_counts).length > 0 && (
              <div className={`flex flex-wrap gap-1 mt-2 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                {Object.entries(message.reaction_counts).map(
                  ([emoji, count]) =>
                    count > 0 && (
                      <button key={emoji} disabled className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                        {`${emoji} ${count}`}
                      </button>
                    )
                )}
              </div>
            )}

            {/* Timestamp — only shown on tap, like iMessage */}
            {showTimestamp && (
              <div className={`text-xs text-gray-500 mt-1 ${isOwn ? 'text-right' : 'text-left'} animate-fade-in`}>
                {formatDateWithLocale(new Date(message.created_at))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Video Message Content
// ---------------------------------------------------------------------------
interface ContentHelpers {
  message: UnifiedMessage;
  isOwn: boolean;
  findOriginalMessage: (id: string) => UnifiedMessage | undefined;
  renderReplyPreview: (msg: UnifiedMessage) => React.ReactNode;
}

function VideoMessageContent({ message, isOwn, findOriginalMessage, renderReplyPreview }: ContentHelpers) {
  const [showPlayer, setShowPlayer] = useState(false);
  const data = message.message_data as Record<string, any> | undefined;
  const videoUrl = data?.video_url as string | undefined;
  const thumbnailUrl = data?.video_thumbnail_url as string | undefined;
  const provider = data?.video_provider as string | undefined;
  const title = data?.video_title as string | undefined;

  const getEmbedUrl = (url: string) => {
    if (!url) return null;
    // YouTube
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    return null;
  };

  const embedUrl = videoUrl ? getEmbedUrl(videoUrl) : null;

  return (
    <div className="space-y-2">
      {message.reply_to_id && (() => {
        const orig = findOriginalMessage(message.reply_to_id);
        return orig ? renderReplyPreview(orig) : null;
      })()}

      {showPlayer && videoUrl ? (
        <div data-interactive className="relative rounded-xl overflow-hidden" style={{ minWidth: 240 }} onTouchStart={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowPlayer(false); }}
            onTouchStart={(e) => { e.stopPropagation(); }}
            onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setShowPlayer(false); }}
            className="absolute top-2 right-2 z-10 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
          >
            <X className="w-4 h-4" />
          </button>
          {embedUrl ? (
            <iframe
              src={embedUrl}
              className="w-full aspect-video rounded-xl"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <video
              src={videoUrl}
              controls
              autoPlay
              className="w-full rounded-xl"
              style={{ maxHeight: 240 }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      ) : (
        <div
          data-interactive
          className="relative rounded-xl overflow-hidden cursor-pointer group"
          style={{ minWidth: 200, minHeight: 120 }}
          onClick={(e) => { e.stopPropagation(); setShowPlayer(true); }}
          onTouchStart={(e) => { e.stopPropagation(); }}
          onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setShowPlayer(true); }}
        >
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={title || 'Video'} className="w-full h-full object-cover rounded-xl" style={{ maxHeight: 180 }} />
          ) : (
            <div className={`w-full h-32 flex items-center justify-center rounded-xl ${isOwn ? 'bg-white/10' : 'bg-gray-100'}`}>
              <Video className={`w-8 h-8 ${isOwn ? 'text-white/60' : 'text-gray-400'}`} />
            </div>
          )}
          {/* Play overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <PlayCircle className="w-7 h-7 text-gray-800" />
            </div>
          </div>
          {provider && (
            <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px] font-medium capitalize">
              {provider}
            </div>
          )}
        </div>
      )}

      {title && (
        <div className={`text-sm font-medium ${isOwn ? 'text-white' : 'text-gray-900'} px-1`}>{title}</div>
      )}
      {message.content && (
        <div className={`text-sm leading-relaxed ${isOwn ? 'text-white/90' : 'text-gray-700'} px-1`}>{message.content}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exercise Share Content
// ---------------------------------------------------------------------------
function ExerciseShareContent({ message, isOwn, findOriginalMessage, renderReplyPreview }: ContentHelpers) {
  const [showPlayer, setShowPlayer] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const data = message.message_data as Record<string, any> | undefined;
  const exerciseName = data?.exercise_name as string | undefined;
  const exerciseCategory = data?.exercise_category as string | undefined;
  const exerciseDescription = data?.exercise_description as string | undefined;
  const exerciseThumbnailUrl = data?.exercise_thumbnail_url as string | undefined;
  const assignmentStatus = data?.assignment_status as string | undefined;
  const playerVideoUrl = data?.player_video_url as string | undefined;

  // Construct a full Exercise object for ExerciseDetailModal (mode="preview")
  const exerciseForModal: Exercise | null = exerciseName ? {
    id: data?.exercise_id ?? '',
    name: exerciseName,
    description: exerciseDescription ?? '',
    category: (exerciseCategory ?? 'short_game') as ExerciseCategory,
    duration_mins: data?.exercise_duration_mins ?? 0,
    difficulty: (data?.exercise_difficulty ?? 'beginner') as ExerciseDifficulty,
    repetitions: data?.exercise_repetitions ?? null,
    equipment_needed: data?.exercise_equipment ?? [],
    thumbnail_url: exerciseThumbnailUrl ?? null,
    video_url: null,
    club_id: null,
    created_by: '',
    is_public: true,
    created_at: '',
    updated_at: '',
  } : null;

  const statusLabel = (s?: string) => {
    switch (s) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'assigned': return 'Assigned';
      default: return s || '';
    }
  };

  const statusColor = (s?: string) => {
    switch (s) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in_progress': return 'text-amber-600 bg-amber-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  const getCategoryStyle = (cat?: string) => {
    const styles: Record<string, { bg: string; text: string; gradient: string }> = {
      driving: { bg: 'bg-red-100', text: 'text-red-700', gradient: 'from-red-500 to-red-600' },
      iron_play: { bg: 'bg-blue-100', text: 'text-blue-700', gradient: 'from-blue-500 to-blue-600' },
      short_game: { bg: 'bg-green-100', text: 'text-green-700', gradient: 'from-green-500 to-green-600' },
      putting: { bg: 'bg-purple-100', text: 'text-purple-700', gradient: 'from-purple-500 to-purple-600' },
      bunker: { bg: 'bg-amber-100', text: 'text-amber-700', gradient: 'from-amber-500 to-amber-600' },
      mental: { bg: 'bg-indigo-100', text: 'text-indigo-700', gradient: 'from-indigo-500 to-indigo-600' },
      fitness: { bg: 'bg-orange-100', text: 'text-orange-700', gradient: 'from-orange-500 to-orange-600' },
      warm_up: { bg: 'bg-teal-100', text: 'text-teal-700', gradient: 'from-teal-500 to-teal-600' },
    };
    return styles[cat || ''] || { bg: 'bg-gray-100', text: 'text-gray-700', gradient: 'from-gray-500 to-gray-600' };
  };

  const getEmbedUrl = (url: string): string | null => {
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    return null;
  };

  const embedUrl = playerVideoUrl ? getEmbedUrl(playerVideoUrl) : null;
  const catStyle = getCategoryStyle(exerciseCategory);
  const modalCatStyle = EXERCISE_CATEGORY_STYLES[exerciseCategory || ''] || DEFAULT_CATEGORY_STYLE;

  return (
    <div className="space-y-2">
      {message.reply_to_id && (() => {
        const orig = findOriginalMessage(message.reply_to_id);
        return orig ? renderReplyPreview(orig) : null;
      })()}

      {/* Exercise card - clickable to open details */}
      <div
        data-interactive
        className={`rounded-xl overflow-hidden border cursor-pointer transition-all hover:shadow-md ${isOwn ? 'border-white/20 bg-white/10 hover:bg-white/15' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}`}
        onClick={(e) => { e.stopPropagation(); setShowDetailModal(true); }}
        onTouchStart={(e) => { e.stopPropagation(); }}
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setShowDetailModal(true); }}
      >
        <div className="p-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isOwn ? 'bg-white/20' : 'bg-indigo-100'}`}>
              <Dumbbell className={`w-4 h-4 ${isOwn ? 'text-white' : 'text-indigo-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold truncate ${isOwn ? 'text-white' : 'text-gray-900'}`}>
                {exerciseName || 'Exercise'}
              </div>
              <div className="flex items-center gap-2">
                {exerciseCategory && (
                  <span className={`text-[10px] capitalize ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                    {exerciseCategory.replace('_', ' ')}
                  </span>
                )}
                {assignmentStatus && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isOwn ? 'text-white/80 bg-white/15' : statusColor(assignmentStatus)}`}>
                    {statusLabel(assignmentStatus)}
                  </span>
                )}
              </div>
            </div>
            <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isOwn ? 'text-white/50' : 'text-gray-400'}`} />
          </div>

          {exerciseDescription && (
            <div className={`text-xs leading-relaxed line-clamp-2 ${isOwn ? 'text-white/80' : 'text-gray-600'}`}>
              {exerciseDescription}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200/50">
            {playerVideoUrl && !showPlayer && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowPlayer(true); }}
                onTouchStart={(e) => { e.stopPropagation(); }}
                onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setShowPlayer(true); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  isOwn
                    ? 'bg-white/20 text-white hover:bg-white/30'
                    : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                }`}
              >
                <PlayCircle className="w-3.5 h-3.5" />
                Watch Video
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setShowDetailModal(true); }}
              onTouchStart={(e) => { e.stopPropagation(); }}
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setShowDetailModal(true); }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                isOwn
                  ? 'bg-white/10 text-white/80 hover:bg-white/20'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Info className="w-3.5 h-3.5" />
              View Details
            </button>
          </div>

          {/* Inline video player */}
          {playerVideoUrl && showPlayer && (
            <div className="mt-2">
              <div className="relative rounded-lg overflow-hidden">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowPlayer(false); }}
                  onTouchStart={(e) => { e.stopPropagation(); }}
                  onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setShowPlayer(false); }}
                  className="absolute top-2 right-2 z-10 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                >
                  <X className="w-3 h-3" />
                </button>
                {embedUrl ? (
                  <iframe
                    src={embedUrl}
                    className="w-full aspect-video rounded-lg"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <video
                    src={playerVideoUrl}
                    controls
                    autoPlay
                    className="w-full rounded-lg"
                    style={{ maxHeight: 200 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {message.content && (
        <div className={`text-sm leading-relaxed ${isOwn ? 'text-white' : 'text-gray-900'} px-1`}>
          {message.content}
        </div>
      )}

      {exerciseForModal && (
        <ExerciseDetailModal
          mode="preview"
          exercise={exerciseForModal}
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          onAdd={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
}

